import { useEffect, useState } from 'react';
import {
    commonQueries,
    useCommonStore,
    useCreatedSearchStore,
    useSearchStore,
} from '@/shared/lib/store';
import { lmsApi } from '@/shared/api/common';
import { EXCLOUD_STUDYMAP_ID } from '@/shared/constants/constants';

export const CreatedBoxSearchFilter = ({
    createdHistoryList,
    selected,
    setSelected,
    setFilterSelected,
}: {
    createdHistoryList?: API.CreatedHistoryList.Res | undefined;
    selected: number[][];
    setSelected: (newSelected: number[][]) => void;
    setFilterSelected: (newFilterSelected: number[][]) => void;
}) => {
    const [openSelectedTextbkId, setOpenSelectedTextbkId] = useState<number>(0);
    const { setSearchPage } = useSearchStore();
    const {
        setOpenSelected,
        openSelected,
        firstFilterData,
        setFirstFilterData,
        filteredData,
        setFilteredData,
    } = useCreatedSearchStore();
    const { prevPath } = useCommonStore();
    const { data: createdCurrList, refetch } = commonQueries.getTextbookCurrList(
        { textbkId: openSelectedTextbkId },
        !!openSelectedTextbkId
    );

    // const [firstFilterData, setFirstFilterData] = useState<DATA.TextbkList[]>([]); // depth0
    // const [filteredData, setFilteredData] = useState<API.TextbookMetaCrcuList.TextbookMeta[][]>([
    //     [], // depth1
    //     [], // depth2
    //     [], // depth3
    // ]);

    // const [openSelected, setOpenSelected] = useState<number[][]>([[], [], [], []]); // 뎁스 오픈 리스트

    const [tags, setTags] = useState<TagType[]>([]);
    const [openSearchFilter, setOpenSearchFilter] = useState(false);
    const [stackCreatedCurrList, setStackCreatedCurrList] = useState<API.TextbookMetaCrcuList.Res>(
        []
    );
    const [first, setFirst] = useState<boolean>(true);

    const pruneTwoLevels = (
        parentLevel: number[],
        childLevel: number[],
        data: API.TextbookMetaCrcuList.Res
    ): number[] => {
        return parentLevel.filter((parentId) => {
            const hasChildWithThisParent = childLevel.some((childId) => {
                const childData = data.find((item) => item.id === childId);
                return childData?.parentId === parentId;
            });
            return !hasChildWithThisParent;
        });
    };

    const recursivePrune = (levels: number[][], data: API.TextbookMetaCrcuList.Res): number[][] => {
        if (levels.length < 2) {
            return levels;
        }

        const prunedParent = pruneTwoLevels(levels[0], levels[1], data);

        const prunedRemaining = recursivePrune(levels.slice(1), data);

        return [prunedParent, ...prunedRemaining];
    };

    useEffect(() => {
        if (prevPath.includes('sets/created-box/detail') || prevPath.includes('/edit')) {
            const fetchDepthData = async () => {
                if (first && selected) {
                    const result: API.TextbookMetaCrcuList.Res = [];
                    if (selected[0] && selected[0].length > 0) {
                        for (const id of selected[0]) {
                            try {
                                const res = await lmsApi.getTextbookMetaCrcuList({
                                    textbkId: id,
                                });
                                if (res) {
                                    result.push(...res);
                                }
                            } catch (error) {
                                console.error('Error fetching meta list:', error);
                            }
                        }
                    }
                    setStackCreatedCurrList(result);

                    const d1 = result.filter(
                        (meta) =>
                            meta.depth === 1 && openSelected[0].includes(Number(meta.textbkId))
                    );
                    const d2 = result.filter((meta) =>
                        openSelected[1].includes(meta.parentId ?? -9999)
                    );
                    const d3 = result.filter((meta) =>
                        openSelected[2].includes(meta.parentId ?? -9999)
                    );

                    const filterList = [d1, d2, d3].map((e, i) =>
                        e.map((el) => {
                            if (result.some((f) => f.parentId === el.id)) {
                                return { ...el, child: true };
                            } else {
                                return { ...el, child: false };
                            }
                        })
                    );
                    setFilteredData(filterList);
                }
                setFirst(false);
            };

            fetchDepthData();
        }
    }, [first, selected, openSelected]);

    useEffect(() => {
        if (stackCreatedCurrList)
            setFilterSelected(
                recursivePrune(
                    [selected[1], selected[2], selected[3]],
                    stackCreatedCurrList ?? [[], [], []]
                )
            );
    }, [selected, stackCreatedCurrList]);

    useEffect(() => {
        if (createdCurrList) {
            const prevList = [...stackCreatedCurrList];
            const newList = [...createdCurrList];
            const stackList = [...new Set([...prevList, ...newList])];
            setStackCreatedCurrList(stackList);
        }
    }, [createdCurrList]);

    useEffect(() => {
        if (createdHistoryList?.textbkList) {
            setFirstFilterData(createdHistoryList.textbkList);
        }
    }, [createdHistoryList]);

    // ─────────────────────────────────────────────────────────
    // (2) 1~3뎁스 데이터 필터링
    //  - depth1: parentId in selected[0] (주의: selected[0]은 textbkId)
    //  - depth2: parentId in selected[1]
    //  - depth3: parentId in selected[2]
    // ─────────────────────────────────────────────────────────
    useEffect(() => {
        if (!createdCurrList) {
            setFilteredData([[], [], []]);
            return;
        }

        const d1 = createdCurrList.filter(
            (meta) =>
                meta.depth === 1 &&
                openSelected[0].includes(Number(meta.textbkId)) &&
                !EXCLOUD_STUDYMAP_ID.includes(meta.id)
        );
        const d2 = createdCurrList.filter(
            (meta) =>
                openSelected[1].includes(meta.parentId ?? -9999) &&
                !EXCLOUD_STUDYMAP_ID.includes(meta.id)
        );
        const d3 = createdCurrList.filter(
            (meta) =>
                openSelected[2].includes(meta.parentId ?? -9999) &&
                !EXCLOUD_STUDYMAP_ID.includes(meta.id)
        );

        const filterList = [d1, d2, d3].map((e, i) =>
            e.map((el) => {
                if (createdCurrList.some((f) => f.parentId === el.id)) {
                    return { ...el, child: true };
                } else {
                    return { ...el, child: false };
                }
            })
        );
        setFilteredData(filterList);
    }, [createdCurrList, openSelected]);

    // ─────────────────────────────────────────────────────────
    // (3) 태그 만들기
    // ─────────────────────────────────────────────────────────
    useEffect(() => {
        if (!stackCreatedCurrList) return;
        const paths = buildMaxDepthPaths(selected, firstFilterData, stackCreatedCurrList);
        const newTags: TagType[] = [];

        for (const p of paths) {
            // if (p.length > 1) {
            // label 붙이기
            const label = p
                .map((id, depthIndex) => {
                    if (depthIndex === 0) {
                        // 0뎁스 -> tbkList
                        return firstFilterData.find((t) => t.textbkId === id)?.curriBookNm ?? '';
                    } else {
                        // metaList
                        return stackCreatedCurrList.find((m) => m.id === id)?.val ?? '';
                    }
                })
                .join(' > ');

            newTags.push({ ids: p, label });
            // }
        }
        setTags(newTags);
    }, [selected, firstFilterData, stackCreatedCurrList]);

    // ─────────────────────────────────────────────────────────
    // ◆ buildAllPaths
    //  0뎁스는 textbkId => firstFilterData에서 찾기
    //  1~3뎁스는 metaList => createdCurrList에서 찾기
    // ─────────────────────────────────────────────────────────

    function buildMaxDepthPaths(
        sel: number[][],
        tbkList: DATA.TextbkList[], // 0뎁스
        metaList: API.TextbookMetaCrcuList.TextbookMeta[] // 1~3뎁스
    ): number[][] {
        const results: number[][] = [];

        // 0뎁스(교재)부터 순회
        //   -> selected[0] 에는 textbkId가 들어있음
        for (const textbkId of sel[0]) {
            const path = [textbkId];
            // depth=0에서 시작
            dfs(0, textbkId, path);
        }

        function dfs(depth: number, parentId: number, path: number[]) {
            if (depth >= 3) {
                // 3뎁스까지 내려갔는데 더 내려갈 곳이 없으면 현재 경로를 결과에 추가
                results.push([...path]);
                return;
            }

            // 다음 depth
            const nextDepth = depth + 1;
            // 현재 depth가 0이면 parentId는 textbkId
            // 현재 depth가 1~3이면 parentId는 meta.id
            // → 이를 구분해서 자식(= 다음 depth에서 선택된 id 중 parent가 parentId인 것) 을 찾는다.
            let childIds: number[] = [];

            if (depth === 0) {
                // depth=0의 parentId => textbkId
                childIds = sel[nextDepth].filter((id) => {
                    const found = metaList.find((m) => m.id === id);
                    return Number(found?.textbkId) === parentId;
                });
            } else {
                // depth=1 이상 => meta의 parentId가 parentId 인 것
                childIds = sel[nextDepth].filter((id) => {
                    const found = metaList.find((m) => m.id === id);
                    return found?.parentId === parentId;
                });
            }

            if (childIds.length === 0) {
                // 더 내려갈 자식이 없다면 → 지금 path가 "최대 깊이"이므로 결과 등록
                results.push([...path]);
            } else {
                // 자식이 있으면 계속 내려감
                for (const cId of childIds) {
                    path.push(cId);
                    dfs(nextDepth, cId, path);
                    path.pop();
                }
            }
        }

        return results;
    }

    const handleCheckDepth0 = (tbkId: number, isChecked: boolean, isOpen: boolean) => {
        if (!createdHistoryList) return;
        setSearchPage(0);

        // 1) 현재 selected 상태 깊은 복제
        const newSel = structuredClone(selected) as number[][];

        if (isChecked) {
            // [ON] 체크
            if (!newSel[0].includes(tbkId)) {
                newSel[0].push(tbkId);
            }
        } else {
            // [OFF] 체크 해제
            newSel[0] = newSel[0].filter((x) => x !== tbkId);
            if (isOpen) handleOpenDepth(0, tbkId);

            // 자식 해제 재귀 함수
            const removeChildrenRecursively = (depth: number, parentId: number) => {
                if (depth >= newSel.length - 1) return;
                const nextDepth = depth + 1;
                if (!stackCreatedCurrList) return;

                // nextDepth에서 parentId의 자식들만 골라내기
                const childIds = newSel[nextDepth].filter((cid) => {
                    const item = stackCreatedCurrList.find((x) => x.id === cid);
                    return nextDepth === 1
                        ? item?.textbkId === parentId
                        : item?.parentId === parentId;
                });

                if (childIds.length) {
                    // 해당 자식들 제거
                    newSel[nextDepth] = newSel[nextDepth].filter((v) => !childIds.includes(v));
                    // 재귀로 더 깊은 자식들까지 제거
                    childIds.forEach((id) => removeChildrenRecursively(nextDepth, id));
                }
            };

            removeChildrenRecursively(0, tbkId);
        }

        // 2) 변경된 newSel을 상태로 설정
        setSelected(newSel);
    };

    const handleCheckDepth = (
        depthIndex: number,
        metaId: number,
        isChecked: boolean,
        isOpen = false
    ) => {
        if (!stackCreatedCurrList) return;

        // 1) 현재 selected 상태 깊은 복제
        const newSel = structuredClone(selected) as number[][];

        if (isChecked) {
            // [ON] 체크
            if (!newSel[depthIndex].includes(metaId)) {
                newSel[depthIndex].push(metaId);
            }

            // 상위 자동 체크
            let current = metaId;
            let curDepth = depthIndex;
            while (curDepth > 0) {
                const item = stackCreatedCurrList.find((x) => x.id === current);
                if (!item) break;
                const par = curDepth === 1 ? Number(item.textbkId ?? -9999) : item.parentId;
                if (par == null) break;

                if (!newSel[curDepth - 1].includes(par)) {
                    newSel[curDepth - 1].push(par);
                }
                current = par;
                curDepth--;
            }
        } else {
            // [OFF] 체크 해제
            newSel[depthIndex] = newSel[depthIndex].filter((x) => x !== metaId);
            if (isOpen) handleOpenDepth(depthIndex, metaId);

            // 자식 해제 (재귀)
            const removeChildrenRecursively = (d: number, parentId: number) => {
                if (d >= newSel.length - 1) return;
                const childDepth = d + 1;
                const childIds = newSel[childDepth].filter((cid) => {
                    const cItem = stackCreatedCurrList.find((x) => x.id === cid);
                    return cItem?.parentId === parentId;
                });

                if (childIds.length > 0) {
                    newSel[childDepth] = newSel[childDepth].filter((v) => !childIds.includes(v));
                    childIds.forEach((c) => removeChildrenRecursively(childDepth, c));
                }
            };
            removeChildrenRecursively(depthIndex, metaId);
        }

        // 2) 상태 업데이트
        setSelected(newSel);
    };

    // ─────────────────────────────────────────────────────────
    // ◆ (C) 스팬 클릭 → 열기/닫기
    //    한 depth는 하나만 open
    // ─────────────────────────────────────────────────────────
    const handleOpenDepth = (depth: number, itemId: number) => {
        // 1) 현재 openSelected 상태 깊은 복제
        const newOpen = structuredClone(openSelected) as number[][];

        // 2) 해당 depth에 이미 열려 있는지 확인
        const alreadyOpen = newOpen[depth].includes(itemId);

        // 3) 해당 depth 전부 비우기
        newOpen[depth] = [];

        // 4) 열려 있지 않았다면 열기
        if (!alreadyOpen) {
            newOpen[depth].push(itemId);
        }

        // 5) 하위 depth 전부 닫기
        for (let d = depth + 1; d < newOpen.length; d++) {
            newOpen[d] = [];
        }

        // 6) 상태 업데이트
        setOpenSelected(newOpen);
    };

    type TagType = {
        ids: number[];
        label: string;
    };

    // ----------------------------
    // (1) 태그 삭제 핸들러
    // ----------------------------
    const handleRemoveTag = (tag: TagType) => {
        if (!stackCreatedCurrList) return;

        // 1) 현재 selected 상태 깊은 복제
        const newSel = structuredClone(selected) as number[][];

        // 2) 깊이 순회하며 제거 로직
        for (let d = tag.ids.length - 1; d >= 0; d--) {
            const currId = tag.ids[d];

            // 이미 제거된 항목이면 건너뛰기
            if (!newSel[d]?.includes(currId)) continue;

            // (A) 다른 경로에서 여전히 사용 중인지 확인
            const stillUsed = isNodeStillUsed(d, currId, newSel, stackCreatedCurrList);
            if (stillUsed) continue;

            // (B) 진짜 제거
            newSel[d] = newSel[d].filter((x) => x !== currId);

            if (d > 0) {
                // (C) 부모 제거 여부 검사
                const parentId = findParentId(d, currId, stackCreatedCurrList);
                if (parentId != null) {
                    const parentUsed = isParentStillUsed({
                        parentId,
                        parentDepth: d - 1,
                        newSel,
                        metaList: stackCreatedCurrList,
                    });
                    if (!parentUsed) {
                        newSel[d - 1] = newSel[d - 1].filter((x) => x !== parentId);
                    }
                }
            } else {
                // (D) depth=0(교재)일 때 자식 사용 여부 체크
                const textbkId = currId;
                const usedByChildren = checkTextbkUsedByChildren(
                    textbkId,
                    newSel,
                    stackCreatedCurrList
                );
                if (usedByChildren) {
                    // 복원
                    if (!newSel[0].includes(textbkId)) {
                        newSel[0].push(textbkId);
                    }
                }
            }
        }

        // 3) selected 상태 업데이트
        setSelected(newSel);

        // 4) tags 상태도 직접 복제해서 갱신
        const newTags = tags.filter((t) => t !== tag);
        setTags(newTags);
    };

    // ----------------------------
    // (2) 해당 노드가 여전히 쓰이나?
    //   - (a) 더 깊은 depth(자식·후손)에서 '조상'으로 참조?
    //   - (b) 중복 노드가 있는지? (동일 depth에 똑같은 id는 없다고 가정)
    // ----------------------------
    function isNodeStillUsed(
        depth: number,
        nodeId: number,
        newSel: number[][],
        metaList: API.TextbookMetaCrcuList.TextbookMeta[]
    ): boolean {
        // 현재 depth에 nodeId가 있는지? => 이미 알고 있음(호출부에서 확인)
        // → 더 깊은 depth(= depth+1..끝)에 "조상(ancestor)" 으로 nodeId가 쓰이는 노드가 있는지 검사
        for (let d = depth + 1; d < newSel.length; d++) {
            for (const childId of newSel[d]) {
                if (isAncestor(nodeId, depth, childId, d, metaList)) {
                    return true;
                }
            }
        }
        return false;
    }

    // ----------------------------
    // (3) parent(조상) 노드가
    //     동일 depth + 하위 depth에서
    //     쓰이는지 검사
    // ----------------------------
    function isParentStillUsed(params: {
        parentId: number;
        parentDepth: number;
        newSel: number[][];
        metaList: API.TextbookMetaCrcuList.TextbookMeta[];
    }): boolean {
        const { parentId, parentDepth, newSel, metaList } = params;

        // (a) 동일 depth에서 parentId 자체가 남아있으면 => 사용 중
        if (newSel[parentDepth].includes(parentId)) {
            return true;
        }

        // (b) 더 깊은 depth(= parentDepth+1..끝)에서
        //     parentId를 조상으로 사용하는 노드가 하나라도 있으면 => 사용 중
        for (let d = parentDepth + 1; d < newSel.length; d++) {
            for (const nodeId of newSel[d]) {
                if (isAncestor(parentId, parentDepth, nodeId, d, metaList)) {
                    return true;
                }
            }
        }
        return false;
    }

    // ----------------------------
    // (4) childId ~ root까지 거슬러 올라가
    //     ancestorId에 닿으면 true
    // ----------------------------
    function isAncestor(
        ancestorId: number,
        ancestorDepth: number,
        childId: number,
        childDepth: number,
        metaList: API.TextbookMetaCrcuList.TextbookMeta[]
    ): boolean {
        let currDepth = childDepth;
        let curr = childId;

        while (currDepth > ancestorDepth) {
            const parId = findParentId(currDepth, curr, metaList);
            if (!parId) return false;
            currDepth--;
            curr = parId;
        }
        return curr === ancestorId;
    }

    // ----------------------------
    // (5) parentId 찾기
    //     depth=1 => parent=textbkId
    //     depth>=2 => parent=meta.parentId
    // ----------------------------
    function findParentId(
        depth: number,
        nodeId: number,
        metaList: API.TextbookMetaCrcuList.TextbookMeta[]
    ): number | null {
        if (depth === 0) return null; // 교재(부모 없음)
        const found = metaList.find((m) => m.id === nodeId);
        if (!found) return null;

        if (depth === 1) {
            return Number(found.textbkId ?? -999);
        } else {
            return found.parentId ?? null;
        }
    }

    // ----------------------------
    // (6) depth=0(교재) 삭제 여부:
    //     자식(=depth=1..n)에서 textbkId=... 로 쓰고 있으면 => keep
    // ----------------------------
    function checkTextbkUsedByChildren(
        textbkId: number,
        newSel: number[][],
        metaList: API.TextbookMetaCrcuList.TextbookMeta[]
    ): boolean {
        // depth=1 중 meta.textbkId===textbkId
        for (const d1Id of newSel[1] ?? []) {
            const item = metaList.find((m) => m.id === d1Id);
            if (item?.textbkId === textbkId) {
                return true;
            }
        }
        return false;
    }

    return (
        <div className="borderBox">
            <div className={`depthBox accordionBox base-box ${!openSearchFilter && 'on'}`}>
                {/* 노출 되는 정보값에 따라 클래스 on 추가 24.03.11 */}
                <div className="depth-1">
                    <ul className="scroll-box">
                        {createdHistoryList?.textbkList.map((item, i) => {
                            const checked = selected[0].includes(item.textbkId);
                            const isOpen = openSelected[0].includes(item.textbkId);

                            return (
                                <li key={`depth1-${i}`}>
                                    <input
                                        type="checkbox"
                                        id={`search1-${i + 1}`}
                                        name="depth1"
                                        value={`search1-${i + 1}`}
                                        checked={checked}
                                        onChange={(e) => {
                                            handleCheckDepth0(
                                                item.textbkId,
                                                e.target.checked,
                                                isOpen
                                            );
                                        }}
                                    />
                                    <label htmlFor={`search1-${i + 1}`}>{item.curriBookNm}</label>
                                    {checked && (
                                        <button
                                            type="button"
                                            className={`depth_check ${isOpen && 'on'}`}
                                            onClick={() => {
                                                setOpenSelectedTextbkId(item.textbkId);
                                                handleOpenDepth(0, item.textbkId);
                                            }}
                                        ></button>
                                    )}
                                </li>
                            );
                        })}
                    </ul>
                </div>
                {filteredData.map((e, i) => {
                    const noBorder =
                        i === 2 &&
                        filteredData[1].length > 0 &&
                        !filteredData[1].some((item) => item.child);
                    return (
                        // 노출 되는 정보값에 따라 클래스 on 추가 24.03.26
                        <div
                            key={`curr-depth-${i + 2}`}
                            className={`depth-${i + 2} ${!noBorder && 'on'}`}
                        >
                            <ul className="scroll-box">
                                {e.map((el, il) => {
                                    const checked = selected[i + 1]?.includes(el.id);
                                    const isOpen = openSelected[i + 1]?.includes(el.id);
                                    return (
                                        <li key={`depth-contents-${i + 2}-${il}`}>
                                            <input
                                                type="checkbox"
                                                id={`search${i + 2}-${il}`}
                                                name={`depth${i + 2}` + il}
                                                value={el.id}
                                                checked={checked}
                                                onChange={(e) =>
                                                    handleCheckDepth(
                                                        i + 1,
                                                        el.id,
                                                        e.target.checked,
                                                        isOpen
                                                    )
                                                }
                                            />
                                            <label htmlFor={`search${i + 2}-${il}`}>{el.val}</label>
                                            {checked && el?.child && (
                                                <button
                                                    type="button"
                                                    className={`depth_check ${isOpen && 'on'}`}
                                                    onClick={() => handleOpenDepth(i + 1, el.id)}
                                                ></button>
                                            )}
                                        </li>
                                    );
                                })}
                            </ul>
                        </div>
                    );
                })}
            </div>
            <div className={`accordionBtn depthBoxbtn ${!openSearchFilter && 'on'}`}>
                <button
                    type="button"
                    className="accordion"
                    onClick={(e) => setOpenSearchFilter(false)}
                >
                    <span className="arrowpd">
                        접기
                        <i></i>
                    </span>
                </button>
            </div>
            {/* 검색 필터 */}
            <div className={`accordionBtn searchfilterBoxbtn ${!openSearchFilter && 'on'}`}>
                <button
                    type="button"
                    className="accordion"
                    onClick={(e) => setOpenSearchFilter(true)}
                >
                    <span className="arrowpd">
                        단원 상세 검색
                        <i></i>
                    </span>
                </button>
            </div>
            <div className={`accordionBox base-box searchfilterBox ${tags.length > 0 && 'on'}`}>
                <div className="search-form">
                    <div className="tag-box filter_tag-box">
                        {tags.map((e, i) => (
                            <div className="tag-item" key={`tag-${i}`}>
                                <span className="tag-txt">{e?.label}</span>
                                <button
                                    type="button"
                                    className="del-btn"
                                    onClick={() => handleRemoveTag(e)}
                                >
                                    <span className="sr-only">삭제</span>
                                </button>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
};
